import axios from 'axios';
import tokenService from './tokenService';
import storageService from './storageService';
import API_CONFIG, { buildApiUrl, getEndpoint } from '../config/apiConfig';

class ApiService {
  constructor() {
    this.setupInterceptors();
  }

  setupInterceptors() {
    axios.interceptors.request.use(
      async (config) => {
        if (config.skipAuth ||
            (config.data instanceof FormData && config.data.get('operation') === 'LoginForAddin')) {
        } else {
          const token = await tokenService.getValidAccessToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        config.headers.Accept = 'application/json';
        config.withCredentials = true;
        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  async makeRequest(config) {
    try {
      return await this.makeRequestWithRetry(config);
    } catch (error) {
      return this.handleError(error);
    }
  }

  async makeRequestWithRetry(config, isRetry = false) {
    try {
      const response = await axios(config);
      
      // Check if response indicates token expired
      if (response.data &&
          response.data.success === false &&
          (response.data.error?.message === 'Token Expired' || response.data.message === 'Token Expired') &&
          !isRetry) {
        
        
        // Try to refresh token
        const refreshResult = await this.refreshToken();
        if (refreshResult.success) {
          // Update the authorization header with new token
          const newToken = await tokenService.getValidAccessToken();
          if (newToken) {
            config.headers.Authorization = `Bearer ${newToken}`;
          }
          // Retry the original request
          return await this.makeRequestWithRetry(config, true);
        } else {
          return { success: false, error: 'Token refresh failed', needsLogout: true };
        }
      }
      
      return this.handleResponse(response);
    } catch (error) {
      throw error;
    }
  }

  async refreshToken() {
    try {
      const formData = new FormData();
      formData.append('operation', 'RefreshAccessTokenForAddin');

      const response = await fetch(buildApiUrl(getEndpoint('OUTLOOK_API')), {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        if (data.success) {
          // Extract from result object
          const result = data.result || data;
          const newToken = result.token || result.access_token;
          const expiresIn = result.expires_in || 3600;

          if (newToken) {
            // Store new token
            tokenService.setAccessToken(newToken, expiresIn);

            try {
              const user = await storageService.getUser();
              if (user) {
                user.token = newToken;
                await storageService.saveUser(user);
              }

              const { default: authService } = await import('./authService');
              authService.updateCurrentUserToken(newToken);
            } catch (storageError) {
            }

            return { success: true, token: newToken };
          } else {
            return { success: false, error: 'No token received' };
          }
        } else {
          const errorMessage = data.error?.message || data.message || 'Token refresh failed';
          return { success: false, error: errorMessage };
        }
      } else {
        return { success: false, error: `HTTP ${response.status}` };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  handleResponse(response) {
    const responseData = response.data;

    if (responseData.success === false) {
      // Extract error message from new format
      const errorMessage = responseData.error?.message || responseData.message || 'Unknown error';

      // Check for Invalid Token specifically
      if (errorMessage === 'Invalid Token') {
        // Clear tokens and redirect to login
        tokenService.clearAllTokens();

        // Import authService dynamically to avoid circular dependency
        import('./authService').then(({ default: authService }) => {
          authService.logout();
        });

        return { success: false, data: responseData, error: errorMessage, needsLogout: true };
      }

      return { success: false, data: responseData, error: errorMessage };
    }

    // For successful responses, extract data from result field
    return { success: true, data: responseData.result || responseData };
  }

  handleError(error) {
    let errorMessage = 'Something went wrong. Please try again later.';

    if (error.response) {
      // Handle new error format: { success: false, error: { message: "..." } }
      errorMessage = error.response.data?.error?.message ||
                     error.response.data?.message ||
                     `Server error: ${error.response.status}`;
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
    }

    return { success: false, error: errorMessage };
  }

  // Authentication APIs
  async login(username, password) {
    const formData = new FormData();
    formData.append('operation', 'LoginForAddin');
    formData.append('username', username);
    formData.append('password', password);
    formData.append('mode', 'FORM');

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      skipAuth: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async loginWithOffice365(profile, accessToken) {
    const formData = new FormData();
    formData.append('operation', 'LoginForAddin');
    formData.append('username', profile.email);
    formData.append('mode', 'SSO');
    formData.append('password', accessToken);

    
    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      skipAuth: true,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }


  // Logout API
  async logout() {
    const formData = new FormData();
    formData.append('operation', 'LogoutForAddin');

    try {
      return await this.makeRequest({
        method: 'POST',
        url: buildApiUrl(getEndpoint('OUTLOOK_API')),
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
      // Even if logout API fails, we should clear local data
      return { success: true };
    }
  }

  async getModulesAndFields() {
    const formData = new FormData();
    formData.append('operation', 'GetMetaDataForAddin');

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Record APIs
  async getRecordDetailFromEmail(email) {
    const formData = new FormData();
    formData.append('operation', 'SearchEmailForAddin');
    formData.append('email', email);

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async getRecordDetails(recordId) {
    const formData = new FormData();
    formData.append('operation', 'GetRecordDetailsForAddin');
    formData.append('recordid', recordId);

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async saveRecord(formDataObj) {
    const formData = new FormData();
    formData.append('operation', 'SaveRecordForAddin');
    formData.append('element', JSON.stringify(formDataObj));

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async searchRecords(module, searchValue, source = 'general') {
    const formData = new FormData();
    formData.append('operation', 'SearchRecordForAddin');
    
    const searchData = {
      module: module,
      search_value: searchValue,
      source: source
    };
    formData.append('element', JSON.stringify(searchData));

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Related Records APIs
  async getRelatedTabModules(module, record) {
    const authService = require('./authService').default;
    const relatedTabs = authService.getRelatedTabs(module);
    
    return {
      success: true,
      data: {
        data: relatedTabs
      }
    };
  }

  async getRelatedRecords(recordId, relationId, module, page = 0, limit = 10) {
    const formData = new FormData();
    formData.append('operation', 'GetRelatedRecordsForAddin');

    const requestData = {
      recordid: recordId,
      page: page,
      module: module,
      limit: limit,
      relationid: relationId
    };

    formData.append('element', JSON.stringify(requestData));

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async getSpecialRelatedRecords(operation, recordId, relationId, module, page = 0, limit = 10) {
    const formData = new FormData();
    formData.append('operation', `${operation}ForAddin`);
    
    const requestData = {
      recordid: recordId,
      page: page,
      module: module,
      limit: limit,
      relationid: relationId
    };
    
    formData.append('element', JSON.stringify(requestData));

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async getRelatedComments(recordId, page = 1) {
    const formData = new FormData();
    formData.append('operation', 'GetRelatedCommentsForAddin');
    
    const requestData = {
      recordid: recordId,
      page: page,
      module: 'ModComments',
      limit: 10,
      relationid: 'ModComments'
    };
    
    formData.append('element', JSON.stringify(requestData));

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async saveComment(commentData, attachments = []) {
    const formData = new FormData();
    formData.append('operation', 'SaveRelatedCommentForAddin');
    formData.append('element', commentData.element);
    
    if (attachments && attachments.length > 0) {
      attachments.forEach((file, index) => {
        formData.append(`attachments[${index}]`, file);
        formData.append(`attachments_hidden[${index}]`, file.name);
      });
    }

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('OUTLOOK_API')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  async archiveEmail(emailData) {
    const cleanedEmailData = this.cleanDateTimeFields(emailData);
    
    const emailDataWithRelatedId = {
      ...cleanedEmailData,
      relatedid: cleanedEmailData.parent_id
    };
    
    const formData = new FormData();
    formData.append('operation', 'ArchiveEmailForAddin');
    formData.append('element', JSON.stringify(emailDataWithRelatedId));

    return this.makeRequest({
      method: 'POST',
      url: buildApiUrl(getEndpoint('ARCHIVE_EMAIL')),
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }

  // Utility method
  cleanDateTimeFields(emailData) {
    const cleaned = { ...emailData };
    
    const dateTimeFields = [
      'dateTimeCreated',
      'dateTimeCreatedRaw',
      'createdDate',
      'createdTime'
    ];
    
    dateTimeFields.forEach(field => {
      if (cleaned[field] && typeof cleaned[field] === 'object') {
        if (cleaned[field] instanceof Date) {
          cleaned[field] = cleaned[field].toISOString().replace('T', ' ').substring(0, 19);
        } else if (cleaned[field].toString) {
          cleaned[field] = cleaned[field].toString();
        } else {
          cleaned[field] = JSON.stringify(cleaned[field]).replace(/["\[\]]/g, '');
        }
      }
    });
    
    return cleaned;
  }
}

export default new ApiService();